import React from 'react';
import { Box, Container, Typography, Stack } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TuneIcon from '@mui/icons-material/Tune';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const STEPS_META = [
  { icon: UploadFileIcon, num: '01', titleKey: 'how.step1_title', descKey: 'how.step1_desc', color: '#6366f1' },
  { icon: TuneIcon, num: '02', titleKey: 'how.step2_title', descKey: 'how.step2_desc', color: '#f59e0b' },
  { icon: FileDownloadIcon, num: '03', titleKey: 'how.step3_title', descKey: 'how.step3_desc', color: '#10b981' },
];

export default function HowItWorks() {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        py: { xs: 10, md: 12 },
        bgcolor: 'background.paper',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{
        position: 'absolute', width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%)',
        top: -100, right: -100, pointerEvents: 'none',
      }} />

      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 2, fontSize: { xs: '2rem', md: '2.8rem' },
              background: (theme) => theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #f1f5f9, #818cf8)'
                : 'linear-gradient(135deg, #111827, #6366f1)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}
          >
            {t('how.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" mb={8} maxWidth={500} mx="auto">
            {t('how.subtitle')}
          </Typography>
        </motion.div>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 4, md: 3 }} alignItems="stretch">
          {STEPS_META.map(({ icon: Icon, num, titleKey, descKey, color }, i) => (
            <motion.div
              key={num}
              style={{ flex: 1 }}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
            >
              <Box
                sx={{
                  p: 4, borderRadius: 4, position: 'relative',
                  background: `linear-gradient(160deg, ${color}08 0%, transparent 60%)`,
                  border: `1px solid ${color}18`,
                  height: '100%',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: `0 20px 40px -10px ${color}20`,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '2.5rem', md: '4rem' }, fontWeight: 900, lineHeight: 1,
                    color: `${color}18`,
                    fontFamily: 'Poppins, sans-serif',
                    position: 'absolute', top: 16, right: 20,
                  }}
                >
                  {num}
                </Typography>

                <Box
                  sx={{
                    width: 56, height: 56, borderRadius: 3, mb: 3,
                    background: `linear-gradient(135deg, ${color}22, ${color}44)`,
                    border: `1.5px solid ${color}33`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Icon sx={{ color, fontSize: 28 }} />
                </Box>

                <Typography variant="h6" fontWeight={700} mb={1.5}>{t(titleKey)}</Typography>
                <Typography variant="body2" color="text.secondary" lineHeight={1.7}>{t(descKey)}</Typography>
              </Box>
            </motion.div>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
